// Path: src/components/auth/ActivityLog.tsx
// Updated to fix SVG width and height attributes
// Updated to implement smooth lock animation, fix FirebaseError, and optimize renders
// Updated to implement smooth toggle switch animation, optimize renders, and enhance Firestore error handling

// Path: src/components/auth/ActivityLog.tsx
// Added support for city, region, and ISP from getUserIP
// Updated Collapse to use expandIconPosition="end" per Ant Design deprecation warning
// Optimized timeSpent updates with debouncing
// Added lock icon and enhanced UI/UX
// 05/31/25 05:45 PM +07
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Collapse, message } from "antd";
import { useUserStore } from "../../stores/UserStore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { LockOutlined } from "@ant-design/icons";
import "./ActivityLog.css";

const { Panel } = Collapse;

interface ActivityData {
  userType: string;
  country: string;
  continent: string;
  city?: string;
  region?: string;
  isp?: string;
}

const ActivityLog: React.FC<{ initialCollapsed?: boolean }> = ({ initialCollapsed }) => {
  const userStore = useUserStore();
  const [activityData, setActivityData] = useState<ActivityData | null>(null);
  const [isActivityLoading, setIsActivityLoading] = useState<boolean>(false);
  const [timeSpent, setTimeSpent] = useState<number>(0);
  const [isActivityLogCollapsed, setIsActivityLogCollapsed] = useState<boolean>(
    initialCollapsed !== undefined ? initialCollapsed : !userStore.isLoggedIn
  );
  const lockId = `lock-${uuidv4()}`;
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const sessionStartTime = localStorage.getItem("sessionStartTime");
    if (!sessionStartTime) {
      const startTime = Date.now();
      localStorage.setItem("sessionStartTime", startTime.toString());
      console.log("Session start time recorded:", startTime);
    }

    const updateTime = () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        const startTime = parseInt(localStorage.getItem("sessionStartTime") || "0", 10);
        const currentTime = Date.now();
        const durationInSeconds = Math.floor((currentTime - startTime) / 1000);
        setTimeSpent(durationInSeconds);
      }, 1000);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      clearInterval(interval);
    };
  }, []);

  const fetchActivityData = useCallback(async (retryCount = 0, maxRetries = 3) => {
    setIsActivityLoading(true);
    try {
      const functions = getFunctions();
      const getUserIP = httpsCallable(functions, "getUserIP");
      let uid: string | undefined;

      if (userStore.isLoggedIn && userStore.user) {
        uid = userStore.user.uid;
        console.log("Fetching activity data for authenticated user with UID:", uid);
      } else {
        let publicUserId = localStorage.getItem("publicUserId");
        if (!publicUserId) {
          publicUserId = uuidv4();
          localStorage.setItem("publicUserId", publicUserId);
          console.log("Generated new publicUserId for non-auth user:", publicUserId);
        }
        uid = publicUserId;
      }

      const result = await getUserIP({ uid });
      const data = result.data as any;
      console.log("Fetched activity data:", data);

      setActivityData({
        userType: userStore.isLoggedIn ? userStore.userRole : "OLS prospect",
        country: data.country || "Unknown",
        continent: data.continent || "Unknown",
        city: data.city || undefined,
        region: data.region || undefined,
        isp: data.isp || undefined,
      });

      userStore.setIpData(data);
    } catch (error) {
      console.error("Error fetching activity data:", error, "Stack:", error.stack);
      if (retryCount < maxRetries) {
        console.log(`Retrying fetchActivityData, attempt ${retryCount + 1} of ${maxRetries}`);
        setTimeout(() => fetchActivityData(retryCount + 1, maxRetries), 1000);
      } else {
        message.error("Failed to fetch activity data after retries");
        setActivityData({
          userType: userStore.isLoggedIn ? userStore.userRole : "OLS prospect",
          country: "Unknown",
          continent: "Unknown",
        });
      }
    } finally {
      setIsActivityLoading(false);
      console.log("ActivityLog rendered");
    }
  }, [userStore]);

  useEffect(() => {
    console.log("Effect: Fetching activity data on mount or auth change");
    fetchActivityData();
  }, [fetchActivityData]);

  useEffect(() => {
    console.log("Effect: Updating collapsed state - isLoggedIn:", userStore.isLoggedIn);
    setIsActivityLogCollapsed(!userStore.isLoggedIn);
  }, [userStore.isLoggedIn]);

  const handleLockChange = () => {
    setIsActivityLogCollapsed((prev) => {
      const newState = !prev;
      console.log("Toggle switched, new collapsed state:", newState);
      return newState;
    });
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="activity-log-container">
      <div className="activity-log-card">
        <Collapse
          activeKey={isActivityLogCollapsed ? [] : ["1"]}
          expandIconPosition="end" // Updated from "right" to "end"
          expandIcon={null}
        >
          <Panel
            header={
              <div className="cl-toggle-switch">
                <label className="cl-switch">
                  <input
                    type="checkbox"
                    id={lockId}
                    checked={!isActivityLogCollapsed}
                    onChange={handleLockChange}
                    aria-label={isActivityLogCollapsed ? "Expand Activity Log" : "Collapse Activity Log"}
                  />
                  <span>
                    <LockOutlined style={{ color: "#001f3f", marginRight: "8px" }} />
                  </span>
                </label>
              </div>
            }
            key="1"
          >
            {isActivityLoading ? (
              <div>Loading activity data...</div>
            ) : activityData ? (
              <div>
                <p>
                  <strong>User Type:</strong> {activityData.userType}
                </p>
                <p>
                  <strong>Duration:</strong> {formatDuration(timeSpent)}
                </p>
                <p>
                  <strong>Location:</strong> {activityData.country} ({activityData.continent})
                  {activityData.city && `, ${activityData.city}`}
                  {activityData.region && `, ${activityData.region}`}
                </p>
                {activityData.isp && (
                  <p>
                    <strong>ISP:</strong> {activityData.isp}
                  </p>
                )}
                <p>
                  <strong>Profile:</strong>{" "}
                  <Link to="/register" style={{ color: "#ffaa00", textDecoration: "underline" }}>
                    Register Now
                  </Link>
                </p>
              </div>
            ) : (
              <p>Failed to load activity data</p>
            )}
          </Panel>
        </Collapse>
      </div>
    </div>
  );
};

export default ActivityLog;